import { MapSection } from "./chatbot.types";
import { ChatbotScreen, CHATBOT_MAP_TYPES_WITH_TIPS } from "./chatbot-screens.config";
import {
  MapWidgetInfo,
  MenuWidgetInfo,
  ScreenWidget,
  ScreenWidgetInfo,
  TableWidgetInfo
} from "./chatbot-screen-registry.service";

/** Feature kinds detected on a screen, used to describe its interactions. */
export type ScreenFeature = "table" | "map" | "chart" | "date" | "tabs" | "form";

/** Element-level help exposed by HelpDirective (`data-chatbot-help`). */
export interface ScreenHelpItem {
  label: string | null;
  help: string;
}

/** Page or dialog area: detected features, element help items and widgets. */
export interface ScreenArea {
  features: ScreenFeature[];
  helpItems: ScreenHelpItem[];
  widgets: ScreenWidgetInfo[];
}

/** Topmost open dialog: a screen area plus its title. */
export interface ScreenDialog extends ScreenArea {
  title: string | null;
}

/** What the user currently sees: the routed page and the topmost dialog. */
export interface ScreenSnapshot {
  page: ScreenArea;
  /** Topmost open dialog / popup, if any. */
  dialog: ScreenDialog | null;
}

/** Concept-map section matched to the current URL. */
export interface ScreenSectionMatch {
  section: MapSection;
  /** False when only the top-level area (first route segment) matched. */
  exact: boolean;
}

/** Minimal shape of a side-menu entry used for URL matching. */
export interface MenuEntryLike {
  name?: string;
  link?: string | null;
  help?: string;
  submenu?: MenuEntryLike[];
}

/** Everything needed to build the screen-help reply for the current URL. */
export interface ScreenHelpContext {
  screen: ChatbotScreen | null;
  menuEntry: { name: string; help: string | null } | null;
  section: ScreenSectionMatch | null;
  snapshot: ScreenSnapshot;
}

/** Must return the key itself when there is no translation. */
export type TranslateFn = (key: string, params?: Record<string, string>) => string;

const FEATURE_SELECTORS: ReadonlyArray<[ScreenFeature, string]> = [
  ["table", "app-table-controller, table"],
  ["map", "app-map-controller, app-map3d, .leaflet-container"],
  ["chart", "app-graph-controller, app-graph, .highcharts-container"],
  ["date", "app-material-date-selector"],
  ["tabs", "app-tab-panel, mat-tab-group"],
  ["form", "form"]
];

const DIALOG_SELECTOR = "mat-dialog-container, .mat-mdc-dialog-container, .swal2-popup";
const DIALOG_TITLE_SELECTOR =
  ".dialog-header .dialog-title, .dialog-header .modal-title, [mat-dialog-title], .mat-mdc-dialog-title, .swal2-title, .dialog-header h1, .dialog-header h2, .dialog-header h3, .dialog-header h4, .dialog-header h5";
const HELP_ATTR = "data-chatbot-help";
/** Global chrome that is not part of the current screen. */
const CHROME_SELECTOR = "app-navbar-top, app-navbar-side, app-chatbot";
const MAX_HELP_ITEMS = 8;
const MAX_LABEL_LENGTH = 60;
const MAX_LISTED = 8;
const MAX_TABLES = 3;
const MAX_TABLE_ACTIONS = 4;
const MAX_MENUS = 6;

function cleanText(text: string | null | undefined): string {
  return (text ?? "")
    .replaceAll(String.raw`\n`, " ")
    .replaceAll(String.raw`\t`, " ")
    .replaceAll(/\s+/g, " ")
    .trim();
}

function routeSegments(url: string): string[] {
  return url.split(/[?#]/)[0].split("/").filter(Boolean);
}

// ANCHOR: Route matching

/** Curated screen whose pattern matches every URL segment (":x" = any segment). */
export function findScreenForUrl(url: string, screens: ReadonlyArray<ChatbotScreen>): ChatbotScreen | null {
  const segments = routeSegments(url);
  const matches = (pattern: string) => {
    const parts = routeSegments(pattern);
    return parts.length === segments.length && parts.every((p, i) => p.startsWith(":") || p === segments[i]);
  };
  return screens.find(s => s.routes.some(matches)) ?? null;
}

/** Side-menu entry whose link is exactly the current route. */
export function findMenuEntryForUrl(
  url: string,
  menu: ReadonlyArray<MenuEntryLike>
): { name: string; help: string | null } | null {
  const path = routeSegments(url).join("/");
  for (const entry of menu) {
    if (entry.name && entry.link && routeSegments(entry.link).join("/") === path) {
      return { name: entry.name, help: entry.help ?? null };
    }
    const nested = entry.submenu ? findMenuEntryForUrl(url, entry.submenu) : null;
    if (nested) {
      return nested;
    }
  }
  return null;
}

/** Longest section payload that prefixes the URL; else the top-level section of the first segment. */
export function findSectionForUrl(
  url: string,
  sections: ReadonlyArray<MapSection>
): ScreenSectionMatch | null {
  const segments = routeSegments(url);
  if (segments.length === 0) {
    return null;
  }
  let best: MapSection | null = null;
  let bestLength = 0;
  for (const section of sections) {
    const payload = routeSegments(section.payload);
    const isPrefix =
      payload.length > bestLength &&
      payload.length <= segments.length &&
      payload.every((segment, i) => segment === segments[i]);
    if (isPrefix) {
      best = section;
      bestLength = payload.length;
    }
  }
  if (best) {
    return { section: best, exact: true };
  }
  const sameArea = sections.filter(s => routeSegments(s.payload)[0] === segments[0]);
  const area = sameArea.find(s => !s.parentId) ?? sameArea[0];
  return area ? { section: area, exact: false } : null;
}

// ANCHOR: DOM snapshot

function readArea(root: Element, widgets: ScreenWidgetInfo[]): ScreenArea {
  const features = FEATURE_SELECTORS.filter(([, selector]) => root.querySelector(selector)).map(
    ([feature]) => feature
  );
  const helpItems: ScreenHelpItem[] = [];
  const seen = new Set<string>();
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(`[${HELP_ATTR}]`))) {
    if (helpItems.length >= MAX_HELP_ITEMS) {
      break;
    }
    const help = cleanText(el.getAttribute(HELP_ATTR));
    if (!help || seen.has(help) || el.closest(CHROME_SELECTOR)) {
      continue;
    }
    seen.add(help);
    const label = cleanText(el.getAttribute("aria-label") || el.textContent);
    helpItems.push({ label: label && label.length <= MAX_LABEL_LENGTH ? label : null, help });
  }
  return { features, helpItems, widgets };
}

/** Drops chrome widgets and widgets nested inside a table or map (child tables, map menus). */
function topLevelWidgets(widgets: ScreenWidget[]): ScreenWidget[] {
  const containers = widgets.filter(w => w.info.kind !== "menu").map(w => w.host);
  return widgets.filter(
    w => !w.host.closest(CHROME_SELECTOR) && !containers.some(c => c !== w.host && c.contains(w.host))
  );
}

/** Reads what the user currently sees: routed page content and the topmost open dialog. */
export function readScreenSnapshot(doc: Document, widgets: ScreenWidget[] = []): ScreenSnapshot {
  const pageRoot = doc.querySelector("router-outlet")?.parentElement ?? doc.body;
  const dialogs = Array.from(doc.querySelectorAll<HTMLElement>(DIALOG_SELECTOR));
  const top = dialogs.at(-1);
  const visible = topLevelWidgets(widgets);
  const inDialog = (w: ScreenWidget) => dialogs.some(d => d.contains(w.host));

  const page = readArea(
    pageRoot,
    visible.filter(w => pageRoot.contains(w.host) && !inDialog(w)).map(w => w.info)
  );
  const dialog: ScreenDialog | null = top
    ? {
        ...readArea(top, visible.filter(w => top.contains(w.host)).map(w => w.info)),
        title: cleanText(top.querySelector(DIALOG_TITLE_SELECTOR)?.textContent) || null
      }
    : null;
  return { page, dialog };
}

// ANCHOR: Message

function list(values: string[], t: TranslateFn): string {
  const shown = values.slice(0, MAX_LISTED).map(v => t(v));
  const rest = values.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} (+${rest})` : shown.join(", ");
}

function describeTableLines(table: TableWidgetInfo, t: TranslateFn): string[] {
  const key = (name: string) => `chatbot.screen-help.table.${name}`;
  const lines = [`• ${t(key(table.filters ? "intro" : "intro-plain"))}`];
  const sub = (name: string, when: boolean, params?: Record<string, string>) => {
    if (when) {
      lines.push(`   - ${t(key(name), params)}`);
    }
  };
  sub("date-range", table.dateRange);
  sub("special-filter", table.filters && table.specialFilter);
  sub("file-filter", table.filters);
  sub("quick-filters", table.quickFilters);
  sub("row-actions", table.rowActions);
  sub("selection", table.selectable && table.globalActions.some(a => a.needsSelection));

  // Screen-specific actions are only worth mentioning when they carry an explanation.
  const actions = [...table.globalActions]
    .sort((a, b) => Number(a.needsSelection) - Number(b.needsSelection))
    .filter(a => a.help && t(a.help) !== a.help);
  for (const action of actions.slice(0, MAX_TABLE_ACTIONS)) {
    lines.push(`   - ${t(action.title)}: ${t(action.help!)}`);
  }
  const more = table.globalActions.length - Math.min(actions.length, MAX_TABLE_ACTIONS);
  sub("actions-more", more > 0, { count: String(more) });

  sub("row-legend", table.rowLegend.length > 0, { items: list(table.rowLegend, t) });
  sub("columns", table.menu);
  sub("export", table.exportable);
  return lines;
}

function describeMapLines(map: MapWidgetInfo, t: TranslateFn): string[] {
  const tipKey = `chatbot.screen-help.map-type.${map.mapType}`;
  const lines = [
    CHATBOT_MAP_TYPES_WITH_TIPS.has(map.mapType) && t(tipKey) !== tipKey
      ? `• ${t(tipKey)}`
      : t("chatbot.screen-help.feature.map")
  ];
  if (map.drawSelection) {
    lines.push(`   - ${t("chatbot.screen-help.map.draw-selection")}`);
  }
  if (map.layers.length > 1) {
    lines.push(`   - ${t("chatbot.screen-help.map.layers")}`);
  }
  for (const tool of map.tools) {
    lines.push(`   - ${t(`chatbot.screen-help.map.tool.${tool}`)}`);
  }
  return lines;
}

function describeMenuLine(menu: MenuWidgetInfo, t: TranslateFn): string {
  if (menu.options.length === 0) {
    return `• ${t("chatbot.screen-help.menu.button", { title: t(menu.title ?? "") })}`;
  }
  return menu.title
    ? `• ${t("chatbot.screen-help.menu.options", { title: t(menu.title), items: list(menu.options, t) })}`
    : `• ${t("chatbot.screen-help.menu.options-untitled", { items: list(menu.options, t) })}`;
}

function describeArea(area: ScreenArea, t: TranslateFn): string[] {
  const lines: string[] = [];
  const tables = area.widgets.filter((w): w is TableWidgetInfo => w.kind === "table").slice(0, MAX_TABLES);
  const maps = area.widgets.filter((w): w is MapWidgetInfo => w.kind === "map");
  const menus = area.widgets.filter((w): w is MenuWidgetInfo => w.kind === "menu").slice(0, MAX_MENUS);

  for (const menu of menus) {
    lines.push(describeMenuLine(menu, t));
  }
  for (const table of tables) {
    lines.push(...describeTableLines(table, t));
  }
  for (const map of maps) {
    lines.push(...describeMapLines(map, t));
  }
  for (const feature of area.features) {
    const covered = (feature === "table" && tables.length > 0) || (feature === "map" && maps.length > 0);
    if (!covered) {
      lines.push(t(`chatbot.screen-help.feature.${feature}`));
    }
  }
  for (const item of area.helpItems) {
    lines.push(item.label ? `• ${item.label}: ${item.help}` : `• ${item.help}`);
  }
  return lines;
}

function describeScreen(ctx: ScreenHelpContext, t: TranslateFn): string[] {
  const has = (key: string) => t(key) !== key;
  const { screen, menuEntry, section } = ctx;
  if (screen) {
    const base = `chatbot.screens.${screen.id}`;
    const lines = [t("chatbot.screen-help.screen-title", { title: t(`${base}.title`) })];
    if (has(`${base}.objective`)) {
      lines.push(t("chatbot.screen-help.objective", { text: t(`${base}.objective`) }));
    }
    if (has(`${base}.summary`)) {
      lines.push(t("chatbot.screen-help.summary", { text: t(`${base}.summary`) }));
    }
    return lines;
  }
  if (menuEntry) {
    const objective =
      menuEntry.help && has(menuEntry.help)
        ? t(menuEntry.help)
        : section?.exact
          ? t(section.section.descKey)
          : null;
    const lines = [t("chatbot.screen-help.screen-title", { title: t(menuEntry.name) })];
    if (objective) {
      lines.push(t("chatbot.screen-help.objective", { text: objective }));
    }
    return lines;
  }
  if (section?.exact) {
    return [
      t("chatbot.screen-help.screen-title", { title: t(section.section.labelKey) }),
      t("chatbot.screen-help.objective", { text: t(section.section.descKey) })
    ];
  }
  if (section) {
    return [t("chatbot.screen-help.screen-in-area", { area: t(section.section.labelKey) })];
  }
  return [t("chatbot.screen-help.screen-unknown")];
}

/** Builds the bot reply describing the current screen and how to interact with it. */
export function buildScreenHelpMessage(ctx: ScreenHelpContext, t: TranslateFn): string {
  const lines = describeScreen(ctx, t);
  const { dialog, page } = ctx.snapshot;

  if (dialog) {
    lines.push(
      "",
      dialog.title
        ? t("chatbot.screen-help.dialog", { title: dialog.title })
        : t("chatbot.screen-help.dialog-untitled")
    );
  }

  const interactions = describeArea(dialog ?? page, t);
  const tipsKey = ctx.screen ? `chatbot.screens.${ctx.screen.id}.tips` : null;
  if (!dialog && tipsKey && t(tipsKey) !== tipsKey) {
    interactions.unshift(t(tipsKey));
  }

  if (interactions.length > 0) {
    lines.push("", t(dialog ? "chatbot.screen-help.interact-dialog" : "chatbot.screen-help.interact"), ...interactions);
  } else if (!dialog) {
    lines.push("", t("chatbot.screen-help.no-features"));
  }

  lines.push("", t("chatbot.screen-help.footer"));
  return lines.join("\n");
}

// ANCHOR: Rich formatting

/** Inline segment kinds produced by formatScreenHelpMessage. */
export type HelpSegmentKind = "text" | "label" | "quote";
/** Block kinds produced by formatScreenHelpMessage. */
export type HelpBlockKind = "title" | "heading" | "text" | "bullet" | "sub" | "gap" | "footer";

/** A styled piece of text inside a help block. */
export interface HelpSegment {
  kind: HelpSegmentKind;
  text: string;
}

/** One styled block of the formatted help message. */
export interface HelpBlock {
  kind: HelpBlockKind;
  segments: HelpSegment[];
}

/** Short "Label: text" prefix (max 40 chars); "10:30" is not a label. */
const RE_INLINE_LABEL = /^([^:«»]{1,40}?)\s?:\s+(.+)$/s;
const RE_QUOTE = /«([^»]+)»/g;

function quoteSegments(text: string): HelpSegment[] {
  const segments: HelpSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(RE_QUOTE)) {
    if (match.index > last) {
      segments.push({ kind: "text", text: text.slice(last, match.index) });
    }
    segments.push({ kind: "quote", text: match[1] });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    segments.push({ kind: "text", text: text.slice(last) });
  }
  return segments;
}

function lineSegments(text: string): HelpSegment[] {
  const labelled = RE_INLINE_LABEL.exec(text);
  return labelled
    ? [{ kind: "label", text: labelled[1] }, ...quoteSegments(labelled[2])]
    : quoteSegments(text);
}

/** Splits a screen-help message into styled blocks: title, headings, bullets, inline labels, «quotes». */
export function formatScreenHelpMessage(content: string): HelpBlock[] {
  const lines = content.split("\n");
  let lastIndex = lines.length - 1;
  while (lastIndex > 0 && lines[lastIndex].trim() === "") {
    lastIndex--;
  }
  return lines.map((raw, i): HelpBlock => {
    const line = raw.trim();
    if (!line) {
      return { kind: "gap", segments: [] };
    }
    if (i === 0) {
      return { kind: "title", segments: lineSegments(line) };
    }
    if (i === lastIndex) {
      return { kind: "footer", segments: quoteSegments(line) };
    }
    if (line.startsWith("•")) {
      return { kind: "bullet", segments: lineSegments(line.slice(1).trim()) };
    }
    if (/^\s+-\s/.test(raw)) {
      return { kind: "sub", segments: lineSegments(line.slice(1).trim()) };
    }
    if (line.endsWith(":")) {
      return { kind: "heading", segments: [{ kind: "text", text: line.slice(0, -1) }] };
    }
    return { kind: "text", segments: lineSegments(line) };
  });
}
